/**
 * API URL prefix for the admin groups module.
 * Note: axios.defaults.baseURL already includes the "/api" segment,
 * so we only prepend the admin scope here.
 */
export const GROUPS_API_PREFIX = "/admin/groups";

/**
 * Endpoint paths exposed by the admin groups module.
 */
export const GROUPS_ENDPOINTS = {
    last: `${GROUPS_API_PREFIX}/last`,
    search: (substring) => `${GROUPS_API_PREFIX}/search/${encodeURIComponent(substring)}`,
    detail: (groupId) => `${GROUPS_API_PREFIX}/${groupId}`,
    update: (groupId) => `${GROUPS_API_PREFIX}/${groupId}`,
    remove: (groupId) => `${GROUPS_API_PREFIX}/${groupId}`,
    addMember: (groupId) => `${GROUPS_API_PREFIX}/${groupId}/members`,
    updateMember: (memberId) => `${GROUPS_API_PREFIX}/members/${memberId}`,
    removeMember: (memberId) => `${GROUPS_API_PREFIX}/members/${memberId}`,
    transferLeadership: (groupId, memberId) => `${GROUPS_API_PREFIX}/${groupId}/transfer_leadership/${memberId}`,
    walletFreeze: (groupId) => `${GROUPS_API_PREFIX}/${groupId}/wallet/freeze`,
    walletConfiscate: (groupId) => `${GROUPS_API_PREFIX}/${groupId}/wallet/confiscate`,
    walletBankTransfer: (groupId) => `${GROUPS_API_PREFIX}/${groupId}/wallet/bank_transfer`,
    dispatchTransaction: (groupId, transactionId) => `${GROUPS_API_PREFIX}/${groupId}/transactions/${transactionId}/dispatch`,
};

/**
 * Suggested member roles surfaced in the role select.
 * Free text is also accepted server-side.
 */
export const MEMBER_ROLE_OPTIONS = [
    "Leader",
    "Vocaliste",
    "Choriste",
    "Guitariste",
    "Bassiste",
    "Batteur",
    "Pianiste / Clavier",
    "DJ",
    "Manager",
    "Autre",
];

/**
 * Allowed currency codes for bank transfers (matches backend Stripe support).
 */
export const BANK_TRANSFER_CURRENCIES = ["eur", "usd", "gbp", "chf"];

/**
 * Destination options for the bank-transfer dialog.
 */
export const BANK_TRANSFER_DESTINATIONS = [
    { value: "group", label: "Compte du groupe" },
    { value: "member", label: "Compte d'un membre" },
];

/**
 * Transaction subjects that can be redispatched.
 * Matches DISPATCHABLE_SUBJECTS server-side.
 */
export const DISPATCHABLE_SUBJECTS = new Set([
    "contact_fee",
    "reservation_fee",
    "prestige_fee",
    "kantobiz_advance",
]);

/**
 * Display labels for transaction subjects.
 */
export const TRANSACTION_SUBJECT_LABELS = {
    contact_fee: "Frais de contact",
    reservation_fee: "Frais de réservation",
    prestige_fee: "Frais prestige",
    kantobiz_advance: "Avance KantoBiz",
    wallet_confiscation: "Confiscation",
    bank_transfer: "Virement bancaire",
    member_dispatch: "Dispatch membre",
    refund: "Remboursement",
    payout: "Payout",
};

/**
 * Color schemes for transaction subject chips.
 */
export const TRANSACTION_SUBJECT_COLORS = {
    contact_fee: { bg: "rgba(33, 150, 243, 0.15)", color: "rgba(100, 181, 246, 1)", border: "rgba(33, 150, 243, 0.4)" },
    reservation_fee: { bg: "rgba(76, 175, 80, 0.15)", color: "rgba(129, 199, 132, 1)", border: "rgba(76, 175, 80, 0.4)" },
    prestige_fee: { bg: "rgba(156, 39, 176, 0.15)", color: "rgba(186, 104, 200, 1)", border: "rgba(156, 39, 176, 0.4)" },
    kantobiz_advance: { bg: "rgba(255, 152, 0, 0.15)", color: "rgba(255, 183, 77, 1)", border: "rgba(255, 152, 0, 0.4)" },
    wallet_confiscation: { bg: "rgba(244, 67, 54, 0.15)", color: "rgba(229, 115, 115, 1)", border: "rgba(244, 67, 54, 0.4)" },
    bank_transfer: { bg: "rgba(0, 188, 212, 0.15)", color: "rgba(77, 208, 225, 1)", border: "rgba(0, 188, 212, 0.4)" },
    member_dispatch: { bg: "rgba(139, 195, 74, 0.15)", color: "rgba(174, 213, 129, 1)", border: "rgba(139, 195, 74, 0.4)" },
};

/**
 * Default chip color used as a fallback.
 */
export const DEFAULT_CHIP_COLOR = {
    bg: "rgba(255, 255, 255, 0.08)",
    color: "rgba(255, 255, 255, 0.7)",
    border: "rgba(255, 255, 255, 0.2)",
};

/**
 * Tab identifiers for the group detail drawer.
 */
export const GROUP_DETAIL_TAB_IDS = {
    profile: "profile",
    members: "members",
    wallet: "wallet",
    transactions: "transactions",
};

/**
 * Maximum transactions rendered in the transactions tab to keep the UI snappy.
 */
export const TRANSACTIONS_RENDER_LIMIT = 100;

/**
 * Search debounce delay in milliseconds.
 */
export const SEARCH_DEBOUNCE_MS = 350;

/**
 * Right level required to access the confiscate wallet action.
 */
export const SUPER_ADMIN_RIGHT = 2;
